import { useNavigate } from "react-router-dom";
import type { Country } from "../lib/types";
import { getRegionColor } from "../lib/regionColors";

interface CountriesRailProps {
  countries: Country[];
  entitiesByCountry: Record<string, number>;
}

export function CountriesRail({
  countries,
  entitiesByCountry,
}: CountriesRailProps): JSX.Element {
  const navigate = useNavigate();
  const totalCountries = countries.length;

  return (
    <div
      className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden flex flex-col"
      aria-labelledby="countries-h"
    >
      {/* Card header — proto .card-h */}
      <div className="flex items-center gap-2 px-4 py-[13px] border-b border-border-2 shrink-0">
        <h3
          id="countries-h"
          className="text-[12.5px] font-bold tracking-[.03em] uppercase text-muted"
        >
          Countries
        </h3>
        <span className="ml-auto text-[11px] font-semibold text-dim bg-surface-2 rounded-full px-[9px] py-0.5">
          {totalCountries}
        </span>
      </div>

      {/* Desktop: scrollable flat list grouped by region — data-driven */}
      <div
        className="hidden lg:block overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maxHeight: 444 }}
      >
        {[...new Set(countries.map((c) => c.region))].map((region) => {
          const group = countries.filter((c) => c.region === region);
          if (group.length === 0) return null;
          const groupEntities = group.reduce(
            (sum, c) => sum + (entitiesByCountry[c.name] ?? 0),
            0,
          );
          const colorHex = getRegionColor(region, "hex");

          return (
            <div key={region}>
              {/* Sticky group label — proto .rail-grp */}
              <div
                className={[
                  "flex items-center gap-2 px-4 py-[11px] pb-[6px]",
                  "text-[10px] font-bold tracking-[.05em] uppercase text-dim",
                  "sticky top-0 z-[1] bg-surface",
                ].join(" ")}
              >
                <span
                  aria-hidden
                  className="w-[7px] h-[7px] rounded-full shrink-0"
                  style={{ background: colorHex }}
                />
                <span>{region}</span>
                <span className="ml-auto text-dim font-semibold tabular-nums">
                  {groupEntities} · {group.length}
                </span>
              </div>

              {group.map((country) => (
                <button
                  key={country.id}
                  type="button"
                  aria-label={`Filter by ${country.name}`}
                  onClick={() =>
                    navigate(`/entities?country=${encodeURIComponent(country.name)}`)
                  }
                  className={[
                    "flex items-center gap-[9px] w-full px-4 py-[7px]",
                    "text-[13px] min-h-[40px]",
                    "transition-colors hover:bg-surface-2",
                    "border-b border-border-2 last:border-b-0",
                  ].join(" ")}
                >
                  <span className="text-[17px] leading-none shrink-0" aria-hidden>
                    {country.flag ?? "🏳"}
                  </span>
                  <span className="flex-1 text-left text-text">{country.name}</span>
                  <span className="text-[12px] text-muted tabular-nums">
                    {entitiesByCountry[country.name] ?? 0}
                  </span>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Mobile: accordion per region using native <details> */}
      <div className="lg:hidden">
        {[...new Set(countries.map((c) => c.region))].map((region) => {
          const group = countries.filter((c) => c.region === region);
          if (group.length === 0) return null;
          const groupEntities = group.reduce(
            (sum, c) => sum + (entitiesByCountry[c.name] ?? 0),
            0,
          );
          const colorHex = getRegionColor(region, "hex");

          return (
            <details key={region} className="border-b border-border-2 last:border-b-0">
              <summary
                className={[
                  "flex items-center gap-2 px-4 py-3",
                  "text-[10px] font-bold tracking-[.05em] uppercase text-dim",
                  "cursor-pointer list-none min-h-[44px]",
                ].join(" ")}
              >
                <span
                  aria-hidden
                  className="w-[7px] h-[7px] rounded-full shrink-0"
                  style={{ background: colorHex }}
                />
                <span>{region}</span>
                <span className="ml-auto text-dim font-semibold tabular-nums">
                  {groupEntities} · {group.length}
                </span>
              </summary>
              {group.map((country) => (
                <button
                  key={country.id}
                  type="button"
                  aria-label={`Filter by ${country.name}`}
                  onClick={() =>
                    navigate(`/entities?country=${encodeURIComponent(country.name)}`)
                  }
                  className={[
                    "flex items-center gap-[9px] w-full px-4 py-[7px]",
                    "border-b border-border-2 last:border-b-0",
                    "text-[13px] min-h-[44px]",
                    "transition-colors hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="text-[17px] leading-none shrink-0" aria-hidden>
                    {country.flag ?? "🏳"}
                  </span>
                  <span className="flex-1 text-left text-text">{country.name}</span>
                  <span className="text-[12px] text-muted tabular-nums">
                    {entitiesByCountry[country.name] ?? 0}
                  </span>
                </button>
              ))}
            </details>
          );
        })}
      </div>
    </div>
  );
}
